import React, { useMemo, useState, Fragment, createElement } from 'react';
import { Crown, Check, CheckCircle2, ShieldCheck, LayoutGrid, Target, Megaphone, Zap, TrendingUp, ShoppingBag, X, ArrowRight, Clock, Eye, MousePointerClick, DollarSign, ChevronDown, ChevronUp, RotateCcw, CalendarPlus, Rocket, ArrowUpCircle, Mail, Sparkles, BarChart3, Wallet, AlertCircle, Globe, BookOpen, Pin, FileText, Layers } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { ALL_AD_SLOTS } from '../ads/adData';
interface PromotionTabProps {
  showToast: (msg: string) => void;
  setActiveAutoService: (service: string | null) => void;
  setActiveTab?: (tab: string) => void;
}
interface AdOrder {
  id: string;
  type: 'banner' | 'email' | 'pin';
  name: string;
  status: 'active' | 'completed' | 'pending_moderation' | 'pending_payment' | 'approved' | 'rejected' | 'pending';
  startDate: string;
  endDate: string | null;
  price: number;
  views: number;
  clicks: number;
  orders: number;
  income: number;
  rejectionReason?: string;
  moderatedAt?: string;
  // Creative materials
  adTitle?: string;
  adDescription?: string;
  adLink?: string;
  adImageUrl?: string;
}
// Map ALL_AD_SLOTS to astrologer-friendly format
const SLOT_ICON_MAP: Record<string, {
  icon: any;
  iconCls: string;
  activeCls: string;
}> = {
  Banner: {
    icon: LayoutGrid,
    iconCls: 'bg-indigo-100 text-indigo-600',
    activeCls: 'border-indigo-500 bg-indigo-50/50 ring-1 ring-indigo-500'
  },
  Native: {
    icon: Layers,
    iconCls: 'bg-amber-100 text-amber-600',
    activeCls: 'border-amber-500 bg-amber-50/50 ring-1 ring-amber-500'
  },
  Pinned: {
    icon: Pin,
    iconCls: 'bg-emerald-100 text-emerald-600',
    activeCls: 'border-emerald-500 bg-emerald-50/50 ring-1 ring-emerald-500'
  },
  Email: {
    icon: Mail,
    iconCls: 'bg-purple-100 text-purple-600',
    activeCls: 'border-purple-500 bg-purple-50/50 ring-1 ring-purple-500'
  },
  Sticky: {
    icon: Target,
    iconCls: 'bg-rose-100 text-rose-600',
    activeCls: 'border-rose-500 bg-rose-50/50 ring-1 ring-rose-500'
  }
};
export function PromotionTab({
  showToast,
  setActiveAutoService,
  setActiveTab
}: PromotionTabProps) {
  // Dynamic ad slots from shared adData (synced with admin)
  const availableAdSlots = useMemo(() => {
    return ALL_AD_SLOTS.filter(s => s.status === 'active').map(slot => {
      const style = SLOT_ICON_MAP[slot.type] || SLOT_ICON_MAP.Banner;
      const weeklyPrice = Math.round(slot.basePrice / 4);
      const estimatedReach = Math.round(slot.basePrice / 1.5);
      return {
        id: slot.id,
        name: slot.name,
        where: `${slot.page} · ${slot.type} · ${slot.size}`,
        price: `${weeklyPrice.toLocaleString('ru-RU')} ₽ / неделя`,
        priceNum: weeklyPrice,
        reach: `~${estimatedReach.toLocaleString('ru-RU')} показов`,
        requirements: slot.type === 'Pinned' ? 'Профиль заполнен на 80%+' : slot.type === 'Email' ? 'Текст до 200 символов, ссылка на профиль' : `Изображение ${slot.size}, до 50 символов текста`,
        icon: style.icon,
        activeCls: style.activeCls,
        iconCls: style.iconCls,
        slotType: slot.type
      };
    });
  }, []);
  // State
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [showTariffModal, setShowTariffModal] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState<'pro' | 'top' | null>(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedPinCategory, setSelectedPinCategory] = useState<string>('');
  const [selectedPinDuration, setSelectedPinDuration] = useState<'1week' | '2weeks' | '1month'>('1week');
  const [showNewAdModal, setShowNewAdModal] = useState(false);
  const [selectedAdSlot, setSelectedAdSlot] = useState<string | null>(null);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [adOrdersPage, setAdOrdersPage] = useState(1);
  const AD_ORDERS_PER_PAGE = 5;
  // Ad Wizard State
  const [adWizardStep, setAdWizardStep] = useState<1 | 2 | 3>(1);
  const [adMaterials, setAdMaterials] = useState({
    title: '',
    description: '',
    link: '',
    imageUrl: ''
  });
  const [adBalance, setAdBalance] = useState(4500);
  // Mock Data
  const [adOrders, setAdOrders] = useState<AdOrder[]>([{
    id: 'ORD-A-001',
    type: 'banner',
    name: 'Баннер на главной',
    status: 'active',
    startDate: '01.03.2026',
    endDate: '07.03.2026',
    price: 5000,
    views: 12400,
    clicks: 620,
    orders: 18,
    income: 54000,
    adTitle: 'Профессиональный разбор натальной карты',
    adDescription: 'Узнайте свои сильные стороны, кармические задачи и жизненное предназначение. 15 лет опыта, 3000+ консультаций.',
    adLink: '/astrologer/elena-star',
    adImageUrl: 'https://images.unsplash.com/photo-1532968961962-8a0cb3a2d4f0?w=600&h=300&fit=crop'
  }, {
    id: 'ORD-A-002',
    type: 'email',
    name: 'Рассылка по базе',
    status: 'completed',
    startDate: '15.02.2026',
    endDate: '15.02.2026',
    price: 3000,
    views: 10200,
    clicks: 408,
    orders: 12,
    income: 36000,
    adTitle: 'Весенний прогноз 2026 — персональный транзитный отчёт',
    adDescription: 'Специальная акция! Закажите прогноз на весну со скидкой 30%. Узнайте, какие возможности откроются именно для вас.',
    adLink: '/astrologer/elena-star/forecast'
  }, {
    id: 'ORD-A-003',
    type: 'pin',
    name: 'Закреп в каталоге',
    status: 'active',
    startDate: '01.03.2026',
    endDate: '14.03.2026',
    price: 4000,
    views: 8900,
    clicks: 356,
    orders: 9,
    income: 27000,
    adTitle: 'Елена Звёздная — ТОП астролог',
    adDescription: 'Натальные карты, совместимость, прогнозы. Рейтинг 4.9 ★ Более 3000 консультаций.',
    adLink: '/astrologer/elena-star'
  }, {
    id: 'ORD-A-004',
    type: 'banner',
    name: 'Баннер в каталоге',
    status: 'pending_moderation',
    startDate: '10.03.2026',
    endDate: '17.03.2026',
    price: 3500,
    views: 0,
    clicks: 0,
    orders: 0,
    income: 0,
    adTitle: 'Кармический анализ — глубокий разбор',
    adDescription: 'Откройте тайны прошлых жизней и поймите свою миссию в этом воплощении. Индивидуальный подход.',
    adLink: '/astrologer/elena-star/karma',
    adImageUrl: 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=600&h=300&fit=crop'
  }, {
    id: 'ORD-A-005',
    type: 'pin',
    name: 'Закреп «Натальная карта»',
    status: 'pending_payment',
    startDate: '12.03.2026',
    endDate: '19.03.2026',
    price: 2000,
    views: 0,
    clicks: 0,
    orders: 0,
    income: 0,
    moderatedAt: '12.03.2026',
    adTitle: 'Натальная карта Premium — полный разбор личности',
    adDescription: 'PDF-отчёт на 40+ страниц с детальным анализом всех аспектов вашей карты.',
    adLink: '/astrologer/elena-star/natal'
  }, {
    id: 'ORD-A-006',
    type: 'email',
    name: 'Рассылка по базе',
    status: 'rejected',
    startDate: '08.03.2026',
    endDate: null,
    price: 3000,
    views: 0,
    clicks: 0,
    orders: 0,
    income: 0,
    moderatedAt: '08.03.2026',
    rejectionReason: 'Текст рекламы содержит запрещённые обещания. Пожалуйста, уберите гарантии результата и отправьте повторно.',
    adTitle: 'Гарантированное изменение судьбы за 1 консультацию!',
    adDescription: 'Я изменю вашу жизнь за одну сессию. 100% гарантия результата. Запишитесь прямо сейчас!',
    adLink: '/astrologer/elena-star'
  }]);
  const activePlacements = [{
    type: 'Закреп в каталоге',
    category: 'Натальная карта',
    daysLeft: 5,
    views: 1247
  }, {
    type: 'Баннер на главной',
    category: 'Все услуги',
    daysLeft: 3,
    views: 4200
  }];
  // Computed
  const totalAdIncome = adOrders.reduce((sum, o) => sum + o.income, 0);
  const totalAdOrders = adOrders.reduce((sum, o) => sum + o.orders, 0);
  const adOrdersTotalPages = Math.ceil(adOrders.length / AD_ORDERS_PER_PAGE);
  const paginatedAdOrders = adOrders.slice((adOrdersPage - 1) * AD_ORDERS_PER_PAGE, adOrdersPage * AD_ORDERS_PER_PAGE);
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'banner':
        return 'Баннер';
      case 'email':
        return 'Рассылка';
      case 'pin':
        return 'Закреп';
      default:
        return type;
    }
  };
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          label: 'Активен',
          variant: 'success' as const
        };
      case 'completed':
        return {
          label: 'Завершён',
          variant: 'neutral' as const
        };
      case 'pending':
      case 'pending_moderation':
        return {
          label: 'На модерации',
          variant: 'warning' as const
        };
      case 'pending_payment':
        return {
          label: 'Ожидает оплаты',
          variant: 'info' as const
        };
      case 'approved':
        return {
          label: 'Одобрен',
          variant: 'success' as const
        };
      case 'rejected':
        return {
          label: 'Отклонён',
          variant: 'error' as const
        };
      default:
        return {
          label: status,
          variant: 'default' as const
        };
    }
  };
  const handleRepeatOrder = (order: AdOrder) => {
    showToast(`Повторный заказ "${order.name}" создан`);
    console.log(`[Admin Sync] Ad order repeated: ${order.id}`);
  };
  const handleExtendOrder = (order: AdOrder) => {
    showToast(`Продление "${order.name}" оформлено`);
    console.log(`[Admin Sync] Ad order extended: ${order.id}`);
  };
  const handleUpgradeTariff = (tariff: 'pro' | 'top') => {
    setShowTariffModal(false);
    showToast(`Тариф ${tariff === 'pro' ? 'PRO' : 'TOP'} успешно активирован!`);
    console.log(`[Admin Sync] Tariff upgraded to ${tariff}`);
  };
  const handleBuyPin = () => {
    setShowPinModal(false);
    const prices = {
      '1week': 2000,
      '2weeks': 3500,
      '1month': 6000
    };
    showToast(`Закреп "${selectedPinCategory}" оплачен (${prices[selectedPinDuration].toLocaleString('ru-RU')} ₽)`);
    console.log(`[Admin Sync] Pin purchased: ${selectedPinCategory}, duration: ${selectedPinDuration}`);
  };
  return <div className="space-y-6" data-id="element-14272">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2" data-id="element-14273">
        <div data-id="element-14274">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-14275">
            Продвижение профиля
          </h2>
          <p className="text-slate-500 text-sm mt-1" data-id="element-14276">
            Увеличивайте заказы и доход
          </p>
        </div>
      </div>

      {/* Ad Budget */}
      <Card padding="md" className="border-slate-200 bg-white" data-id="element-14277">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-14278">
          <div className="flex-1" data-id="element-14279">
            <div className="flex items-center gap-2 mb-2" data-id="element-14280">
              <div className="w-9 h-9 rounded-xl bg-purple-100 flex items-center justify-center" data-id="element-14281">
                <Wallet className="w-5 h-5 text-purple-600" data-id="element-14282" />
              </div>
              <div data-id="element-14283">
                <h3 className="font-bold text-slate-900 text-sm" data-id="element-14284">
                  Рекламный баланс
                </h3>
                <p className="text-[10px] text-slate-500" data-id="element-14285">
                  Средства для оплаты рекламных размещений
                </p>
              </div>
            </div>
            <div className="flex items-baseline gap-2 mt-3" data-id="element-14286">
              <span className="text-3xl font-bold text-slate-900" data-id="element-14287">
                {adBalance.toLocaleString('ru-RU')} ₽
              </span>
              <Badge variant="success" size="sm" data-id="element-14288">
                доступно
              </Badge>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-xs text-slate-500" data-id="element-14289">
              <span data-id="element-14290">
                Потрачено за месяц:{' '}
                <strong className="text-slate-700" data-id="element-14291">3 500 ₽</strong>
              </span>
              <span data-id="element-14292">
                Остаток:{' '}
                <strong className="text-slate-700" data-id="element-14293">
                  {adBalance.toLocaleString('ru-RU')} ₽
                </strong>
              </span>
            </div>
          </div>
          <div className="flex flex-col gap-2 w-full sm:w-auto" data-id="element-14294">
            <Button variant="primary" size="md" className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 shadow-sm" icon={<Wallet className="w-4 h-4" data-id="element-14296" />} onClick={() => {
            setAdBalance(prev => prev + 5000);
            showToast('Рекламный баланс пополнен на 5 000 ₽ ✓');
          }} data-id="element-14295">
              Пополнить рекламный баланс
            </Button>
            <p className="text-[10px] text-slate-400 text-center sm:text-right" data-id="element-14297">
              Минимум 1 000 ₽
            </p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-start gap-2 text-xs text-slate-500" data-id="element-14298">
          <AlertCircle className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" data-id="element-14299" />
          <span data-id="element-14300">
            Реклама списывается с рекламного баланса. Если средств не хватает —
            пополните баланс перед оформлением заказа.
          </span>
        </div>
      </Card>

      {/* Section 1: Current Status Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-id="element-14301">
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => showToast('Текущий тариф: Базовый')} data-id="element-14302">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14303">Тариф</div>
          <div className="text-lg font-bold text-slate-900 flex items-center gap-1.5" data-id="element-14304">
            Базовый <Crown className="w-4 h-4 text-amber-400" data-id="element-14305" />
          </div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => {
        if (setActiveTab) setActiveTab('stats');else showToast('Позиция #47 в каталоге');
      }} data-id="element-14306">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14307">Позиция</div>
          <div className="text-lg font-bold text-slate-900" data-id="element-14308">#47</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => {
        if (setActiveTab) setActiveTab('stats');else showToast('1 247 просмотров за неделю');
      }} data-id="element-14309">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14310">
            Просмотры
          </div>
          <div className="text-lg font-bold text-slate-900" data-id="element-14311">1 247</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors" onClick={() => {
        if (setActiveTab) setActiveTab('stats');else showToast('Конверсия 8.3%');
      }} data-id="element-14312">
          <div className="text-xs text-slate-500 mb-1 font-medium" data-id="element-14313">
            Конверсия
          </div>
          <div className="text-lg font-bold text-emerald-600" data-id="element-14314">8.3%</div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors bg-emerald-50/50 border-emerald-200" onClick={() => showToast('Доход с продвижения за месяц')} data-id="element-14315">
          <div className="text-xs text-emerald-600 mb-1 font-medium" data-id="element-14316">
            Доход с рекламы
          </div>
          <div className="text-lg font-bold text-emerald-700" data-id="element-14317">
            {totalAdIncome.toLocaleString('ru-RU')} ₽
          </div>
        </Card>
        <Card padding="sm" className="cursor-pointer hover:border-indigo-300 transition-colors bg-indigo-50/50 border-indigo-200" onClick={() => showToast('Заказы с продвижения за месяц')} data-id="element-14318">
          <div className="text-xs text-indigo-600 mb-1 font-medium" data-id="element-14319">
            Заказы с рекламы
          </div>
          <div className="text-lg font-bold text-indigo-700" data-id="element-14320">
            {totalAdOrders}
          </div>
        </Card>
      </div>

      {/* Section 2: CTA Block — How to increase income */}
      <Card padding="md" className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white border-none relative overflow-hidden" data-id="element-14321">
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-white opacity-10 rounded-full blur-2xl" data-id="element-14322"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-14323">
          <div className="flex-1" data-id="element-14324">
            <div className="flex items-center gap-2 mb-2" data-id="element-14325">
              <Sparkles className="w-5 h-5 text-amber-300" data-id="element-14326" />
              <h3 className="text-xl font-bold" data-id="element-14327">Как увеличить доход</h3>
            </div>
            <p className="text-indigo-100 text-sm max-w-lg" data-id="element-14328">
              Перейдите на тариф <strong className="text-white" data-id="element-14329">PRO</strong> и
              получите приоритет в каталоге. По статистике, PRO-астрологи
              получают{' '}
              <strong className="text-white" data-id="element-14330">на 40% больше заказов</strong>.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0" data-id="element-14331">
            <button className="px-5 py-2.5 bg-white text-indigo-700 font-bold text-sm rounded-lg shadow-lg hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2" onClick={() => {
            setSelectedTariff('pro');
            setShowTariffModal(true);
          }} data-id="element-14332">
              <Crown className="w-4 h-4" data-id="element-14333" />
              Перейти на PRO
            </button>
            <button className="px-5 py-2.5 bg-white/20 backdrop-blur-sm text-white font-bold text-sm rounded-lg border-2 border-white/50 hover:bg-white/30 transition-colors flex items-center justify-center gap-2" onClick={() => setShowNewAdModal(true)} data-id="element-14334">
              <Megaphone className="w-4 h-4" data-id="element-14335" />
              Включить рекламу
            </button>
          </div>
        </div>
      </Card>

      {/* Section 3: Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-14336">
        {[{
        icon: Rocket,
        label: 'Запустить рекламу',
        color: 'text-indigo-600 bg-indigo-50',
        action: () => {
          setShowPinModal(true);
          setSelectedPinCategory('Натальная карта');
        }
      }, {
        icon: ArrowUpCircle,
        label: 'Поднять профиль',
        color: 'text-amber-600 bg-amber-50',
        action: () => {
          setShowPinModal(true);
          setSelectedPinCategory('Натальная карта');
        }
      }, {
        icon: Mail,
        label: 'Включить рассылку',
        color: 'text-emerald-600 bg-emerald-50',
        action: () => showToast('Рассылка включена — клиенты получат уведомление о ваших услугах')
      }, {
        icon: Crown,
        label: 'Улучшить тариф',
        color: 'text-purple-600 bg-purple-50',
        action: () => {
          setSelectedTariff('pro');
          setShowTariffModal(true);
        }
      }].map((item, i) => <button key={i} onClick={item.action} className="flex flex-col items-center gap-2 p-4 rounded-xl border border-slate-200 bg-white hover:border-indigo-300 hover:shadow-sm transition-all text-center group" data-id="element-14337">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.color} group-hover:scale-110 transition-transform`} data-id="element-14338">
              <item.icon className="w-5 h-5" data-id="element-14339" />
            </div>
            <span className="text-sm font-medium text-slate-700" data-id="element-14340">
              {item.label}
            </span>
          </button>)}
      </div>

      {/* Section 4: Ad Orders */}
      <Card padding="none" className="overflow-hidden" data-id="element-14341">
        <div className="p-4 sm:p-5 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3" data-id="element-14342">
          <div data-id="element-14343">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-14344">
              Мои рекламные заказы
            </h3>
            <button onClick={() => setShowHowItWorks(!showHowItWorks)} className="text-xs text-indigo-600 font-medium hover:underline mt-0.5" data-id="element-14345">
              {showHowItWorks ? 'Скрыть справку' : 'Как работает реклама?'}
            </button>
          </div>
          <Button variant="primary" size="sm" icon={<Megaphone className="w-4 h-4" data-id="element-14347" />} onClick={() => setShowNewAdModal(true)} data-id="element-14346">
            Новый заказ
          </Button>
        </div>
        {showHowItWorks && <div className="px-4 sm:px-5 py-4 bg-indigo-50/50 border-b border-indigo-100 animate-in fade-in slide-in-from-top-2" data-id="element-14348">
            <h4 className="font-bold text-indigo-900 text-sm mb-2" data-id="element-14349">
              Как работает реклама на платформе
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-indigo-800" data-id="element-14350">
              <div className="bg-white p-3 rounded-lg border border-indigo-100" data-id="element-14351">
                <div className="font-bold mb-1" data-id="element-14352">1. Оформление и оплата</div>
                <p className="text-indigo-600" data-id="element-14353">
                  Выберите формат, загрузите материалы и оплатите с рекламного
                  баланса.
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-indigo-100" data-id="element-14354">
                <div className="font-bold mb-1" data-id="element-14355">2. Модерация (1-12 часов)</div>
                <p className="text-indigo-600" data-id="element-14356">
                  Администратор проверит материалы. Результат придет в системные
                  уведомления (колокольчик).
                </p>
              </div>
              <div className="bg-white p-3 rounded-lg border border-indigo-100" data-id="element-14357">
                <div className="font-bold mb-1" data-id="element-14358">3. Запуск и статистика</div>
                <p className="text-indigo-600" data-id="element-14359">
                  После одобрения реклама запустится автоматически. Отслеживайте
                  просмотры и клики здесь.
                </p>
              </div>
            </div>
          </div>}

        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto" data-id="element-14360">
          <table className="w-full text-left text-sm" data-id="element-14361">
            <thead className="bg-slate-50/50 border-b border-slate-100 text-slate-500" data-id="element-14362">
              <tr data-id="element-14363">
                <th className="px-5 py-3 font-semibold" data-id="element-14364">Услуга</th>
                <th className="px-5 py-3 font-semibold" data-id="element-14365">Тип</th>
                <th className="px-5 py-3 font-semibold" data-id="element-14366">Статус</th>
                <th className="px-5 py-3 font-semibold" data-id="element-14367">Период</th>
                <th className="px-5 py-3 font-semibold text-right" data-id="element-14368">
                  Стоимость
                </th>
                <th className="px-5 py-3 font-semibold text-right" data-id="element-14369">
                  Результат
                </th>
                <th className="px-5 py-3 w-10" data-id="element-14370"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-14371">
              {paginatedAdOrders.map(order => {
              const isExpanded = expandedOrderId === order.id;
              const statusConfig = getStatusConfig(order.status);
              return <Fragment key={order.id} data-id="element-14372">
                    <tr className={`hover:bg-slate-50/80 transition-colors cursor-pointer ${isExpanded ? 'bg-slate-50/80' : ''}`} onClick={() => setExpandedOrderId(isExpanded ? null : order.id)} data-id="element-14373">
                      <td className="px-5 py-3" data-id="element-14374">
                        <div className="font-bold text-slate-900" data-id="element-14375">
                          {order.name}
                        </div>
                        <div className="text-xs text-slate-500 font-mono mt-0.5" data-id="element-14376">
                          {order.id}
                        </div>
                      </td>
                      <td className="px-5 py-3" data-id="element-14377">
                        <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-md" data-id="element-14378">
                          {getTypeLabel(order.type)}
                        </span>
                      </td>
                      <td className="px-5 py-3" data-id="element-14379">
                        <Badge variant={statusConfig.variant} size="sm" data-id="element-14380">
                          {statusConfig.label}
                        </Badge>
                      </td>
                      <td className="px-5 py-3 text-slate-600 text-xs" data-id="element-14381">
                        {order.endDate ? `${order.startDate} — ${order.endDate}` : `С ${order.startDate}`}
                      </td>
                      <td className="px-5 py-3 text-right font-bold text-slate-900" data-id="element-14382">
                        {order.price.toLocaleString('ru-RU')} ₽
                      </td>
                      <td className="px-5 py-3 text-right" data-id="element-14383">
                        <span className="text-xs text-slate-500" data-id="element-14384">
                          {order.clicks} кликов / {order.orders} заказов
                        </span>
                      </td>
                      <td className="px-5 py-3 text-slate-400" data-id="element-14385">
                        {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-14386" /> : <ChevronDown className="w-5 h-5" data-id="element-14387" />}
                      </td>
                    </tr>

                    {isExpanded && <tr className="bg-slate-50/50" data-id="element-14388">
                        <td colSpan={7} className="px-5 py-4" data-id="element-14389">
                          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm animate-in fade-in slide-in-from-top-2" data-id="element-14390">
                            {order.status === 'pending' || order.status === 'pending_moderation' || order.status === 'pending_payment' || order.status === 'rejected' ? <div className="space-y-4" data-id="element-14391">
                                <div className="flex items-center gap-2 mb-2" data-id="element-14392">
                                  {order.status === 'rejected' ? <AlertCircle className="w-5 h-5 text-rose-500" data-id="element-14393" /> : order.status === 'pending_payment' ? <Wallet className="w-5 h-5 text-indigo-500" data-id="element-14394" /> : <AlertCircle className="w-5 h-5 text-amber-500" data-id="element-14395" />}
                                  <h4 className="font-bold text-slate-900 text-sm" data-id="element-14396">
                                    {order.status === 'rejected' ? 'Заказ отклонён' : order.status === 'pending_payment' ? 'Ожидает оплаты' : 'Заказ на модерации'}
                                  </h4>
                                </div>
                                <p className="text-xs text-slate-600" data-id="element-14397">
                                  {order.status === 'rejected' ? 'Модерация не пройдена. Исправьте замечания и отправьте повторно.' : order.status === 'pending_payment' ? 'Модерация пройдена! Оплатите заказ для запуска рекламы.' : 'Ваша реклама проверяется администратором. Обычно это занимает 1–12 часов.'}
                                </p>

                                {order.status === 'rejected' && order.rejectionReason && <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700" data-id="element-14398">
                                      <div className="font-bold mb-1" data-id="element-14399">
                                        Причина отклонения:
                                      </div>
                                      {order.rejectionReason}
                                    </div>}

                                <div className="relative pt-2 pb-2" data-id="element-14400">
                                  <div className="absolute left-3 top-6 bottom-4 w-0.5 bg-slate-200" data-id="element-14401"></div>

                                  {/* Шаг 1: Оформлен */}
                                  <div className="flex gap-3 mb-3 relative z-10" data-id="element-14402">
                                    <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0" data-id="element-14403">
                                      <Check className="w-3 h-3" data-id="element-14404" />
                                    </div>
                                    <div data-id="element-14405">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-14406">
                                        Оформлен
                                      </div>
                                      <div className="text-[10px] text-slate-500" data-id="element-14407">
                                        {order.startDate}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Шаг 2: Модерация */}
                                  <div className="flex gap-3 mb-3 relative z-10" data-id="element-14408">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'rejected' ? 'bg-rose-100 text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-14409">
                                      {order.status === 'rejected' ? <AlertCircle className="w-3 h-3" data-id="element-14410" /> : order.status === 'pending_payment' || order.status === 'approved' ? <Check className="w-3 h-3" data-id="element-14411" /> : <Clock className="w-3 h-3" data-id="element-14412" />}
                                    </div>
                                    <div data-id="element-14413">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-14414">
                                        Модерация
                                      </div>
                                      <div className={`text-[10px] ${order.status === 'rejected' ? 'text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-14415">
                                        {order.status === 'rejected' ? `Отклонено ${order.moderatedAt || ''}` : order.status === 'pending_payment' || order.status === 'approved' ? `Одобрено ${order.moderatedAt || ''}` : 'В процессе...'}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Шаг 3: Оплата */}
                                  <div className={`flex gap-3 mb-3 relative z-10 ${order.status === 'rejected' || order.status === 'pending' || order.status === 'pending_moderation' ? 'opacity-40' : ''}`} data-id="element-14416">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'pending_payment' ? 'bg-indigo-100 text-indigo-600 ring-2 ring-indigo-300 ring-offset-1' : order.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-14417">
                                      <Wallet className="w-3 h-3" data-id="element-14418" />
                                    </div>
                                    <div data-id="element-14419">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-14420">
                                        Оплата
                                      </div>
                                      <div className={`text-[10px] ${order.status === 'pending_payment' ? 'text-indigo-600 font-semibold' : order.status === 'approved' ? 'text-emerald-600' : 'text-slate-500'}`} data-id="element-14421">
                                        {order.status === 'pending_payment' ? 'Ожидает оплаты' : order.status === 'approved' ? 'Оплачено' : 'Ожидается'}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Шаг 4: Запуск */}
                                  <div className={`flex gap-3 relative z-10 ${order.status !== 'approved' ? 'opacity-40' : ''}`} data-id="element-14422">
                                    <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0" data-id="element-14423">
                                      <Rocket className="w-3 h-3" data-id="element-14424" />
                                    </div>
                                    <div data-id="element-14425">
                                      <div className="font-bold text-xs text-slate-900" data-id="element-14426">
                                        Запуск
                                      </div>
                                      <div className="text-[10px] text-slate-500" data-id="element-14427">
                                        Ожидается
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Материалы рекламы */}
                                {(order.adTitle || order.adImageUrl) && <div className="mt-4 pt-4 border-t border-slate-100" data-id="element-14428">
                                    <div className="flex items-center gap-2 mb-3" data-id="element-14429">
                                      <FileText className="w-4 h-4 text-slate-400" data-id="element-14430" />
                                      <h5 className="font-bold text-xs text-slate-700 uppercase tracking-wider" data-id="element-14431">
                                        Материалы рекламы
                                      </h5>
                                    </div>
                                    <div className={`rounded-lg border border-slate-200 overflow-hidden ${order.adImageUrl ? 'flex flex-col sm:flex-row' : ''}`} data-id="element-14432">
                                      {order.adImageUrl && <div className="sm:w-48 h-28 sm:h-auto shrink-0 bg-slate-100" data-id="element-14433">
                                          <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-14434" />
                                        </div>}
                                      <div className="p-3 flex-1 min-w-0" data-id="element-14435">
                                        {order.adTitle && <div className="font-bold text-sm text-slate-900 mb-1 truncate" data-id="element-14436">
                                            {order.adTitle}
                                          </div>}
                                        {order.adDescription && <p className="text-xs text-slate-600 mb-2 line-clamp-2" data-id="element-14437">
                                            {order.adDescription}
                                          </p>}
                                        {order.adLink && <div className="flex items-center gap-1.5 text-[10px] text-indigo-600 font-medium" data-id="element-14438">
                                            <Globe className="w-3 h-3" data-id="element-14439" />
                                            <span className="truncate" data-id="element-14440">
                                              {order.adLink}
                                            </span>
                                          </div>}
                                      </div>
                                    </div>
                                  </div>}

                                {/* Action buttons */}
                                <div className="flex flex-wrap gap-2 pt-2" data-id="element-14441">
                                  {order.status === 'pending_payment' && <Button variant="primary" size="sm" icon={<Wallet className="w-3.5 h-3.5" data-id="element-14443" />} onClick={e => {
                            e.stopPropagation();
                            setAdOrders(prev => prev.map(o => o.id === order.id ? {
                              ...o,
                              status: 'active' as const
                            } : o));
                            setAdBalance(prev => prev - order.price);
                            showToast(`Оплата ${order.price.toLocaleString('ru-RU')} ₽ прошла. Реклама запущена!`);
                          }} data-id="element-14442">
                                      Оплатить{' '}
                                      {order.price.toLocaleString('ru-RU')} ₽
                                    </Button>}
                                  {order.status === 'rejected' && <Button variant="primary" size="sm" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-14445" />} onClick={e => {
                            e.stopPropagation();
                            setAdOrders(prev => prev.map(o => o.id === order.id ? {
                              ...o,
                              status: 'pending_moderation' as const,
                              rejectionReason: undefined
                            } : o));
                            showToast('Заявка отправлена повторно на модерацию');
                          }} data-id="element-14444">
                                      Исправить и отправить
                                    </Button>}
                                </div>
                              </div> : <>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-5" data-id="element-14446">
                                  <div data-id="element-14447">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14448">
                                      <Eye className="w-3 h-3" data-id="element-14449" /> Просмотры
                                    </div>
                                    <div className="text-xl font-bold text-slate-900" data-id="element-14450">
                                      {order.views.toLocaleString('ru-RU')}
                                    </div>
                                  </div>
                                  <div data-id="element-14451">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14452">
                                      <MousePointerClick className="w-3 h-3" data-id="element-14453" />{' '}
                                      Клики
                                    </div>
                                    <div className="text-xl font-bold text-slate-900" data-id="element-14454">
                                      {order.clicks.toLocaleString('ru-RU')}
                                    </div>
                                  </div>
                                  <div data-id="element-14455">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14456">
                                      <ShoppingBag className="w-3 h-3" data-id="element-14457" /> Заказы
                                    </div>
                                    <div className="text-xl font-bold text-indigo-600" data-id="element-14458">
                                      {order.orders}
                                    </div>
                                  </div>
                                  <div data-id="element-14459">
                                    <div className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-1 flex items-center gap-1" data-id="element-14460">
                                      <DollarSign className="w-3 h-3" data-id="element-14461" /> Доход
                                    </div>
                                    <div className="text-xl font-bold text-emerald-600" data-id="element-14462">
                                      {order.income.toLocaleString('ru-RU')} ₽
                                    </div>
                                  </div>
                                </div>

                                <div className="text-xs text-slate-500 mb-4 bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-14463">
                                  CTR:{' '}
                                  <strong className="text-slate-700" data-id="element-14464">
                                    {(order.clicks / order.views * 100).toFixed(1)}
                                    %
                                  </strong>{' '}
                                  · Конверсия:{' '}
                                  <strong className="text-slate-700" data-id="element-14465">
                                    {order.clicks > 0 ? (order.orders / order.clicks * 100).toFixed(1) : 0}
                                    %
                                  </strong>{' '}
                                  · ROI:{' '}
                                  <strong className={order.income > order.price ? 'text-emerald-600' : 'text-rose-600'} data-id="element-14466">
                                    {order.price > 0 ? ((order.income - order.price) / order.price * 100).toFixed(0) : 0}
                                    %
                                  </strong>
                                </div>

                                {/* Материалы рекламы */}
                                {(order.adTitle || order.adImageUrl) && <div className="mb-4 pt-2" data-id="element-14467">
                                    <div className="flex items-center gap-2 mb-3" data-id="element-14468">
                                      <FileText className="w-4 h-4 text-slate-400" data-id="element-14469" />
                                      <h5 className="font-bold text-xs text-slate-700 uppercase tracking-wider" data-id="element-14470">
                                        Материалы рекламы
                                      </h5>
                                    </div>
                                    <div className={`rounded-lg border border-slate-200 overflow-hidden ${order.adImageUrl ? 'flex flex-col sm:flex-row' : ''}`} data-id="element-14471">
                                      {order.adImageUrl && <div className="sm:w-48 h-28 sm:h-auto shrink-0 bg-slate-100" data-id="element-14472">
                                          <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-14473" />
                                        </div>}
                                      <div className="p-3 flex-1 min-w-0" data-id="element-14474">
                                        {order.adTitle && <div className="font-bold text-sm text-slate-900 mb-1 truncate" data-id="element-14475">
                                            {order.adTitle}
                                          </div>}
                                        {order.adDescription && <p className="text-xs text-slate-600 mb-2 line-clamp-2" data-id="element-14476">
                                            {order.adDescription}
                                          </p>}
                                        {order.adLink && <div className="flex items-center gap-1.5 text-[10px] text-indigo-600 font-medium" data-id="element-14477">
                                            <Globe className="w-3 h-3" data-id="element-14478" />
                                            <span className="truncate" data-id="element-14479">
                                              {order.adLink}
                                            </span>
                                          </div>}
                                      </div>
                                    </div>
                                  </div>}

                                <div className="flex gap-2" data-id="element-14480">
                                  <Button variant="primary" size="sm" className="flex-1" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-14482" />} onClick={e => {
                            e.stopPropagation();
                            handleRepeatOrder(order);
                          }} data-id="element-14481">
                                    Повторить
                                  </Button>
                                  {order.status === 'active' && <Button variant="outline" size="sm" className="flex-1" icon={<CalendarPlus className="w-3.5 h-3.5" data-id="element-14484" />} onClick={e => {
                            e.stopPropagation();
                            handleExtendOrder(order);
                          }} data-id="element-14483">
                                      Продлить
                                    </Button>}
                                </div>
                              </>}
                          </div>
                        </td>
                      </tr>}
                  </Fragment>;
            })}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden divide-y divide-slate-100" data-id="element-14485">
          {paginatedAdOrders.map(order => {
          const isExpanded = expandedOrderId === order.id;
          const statusConfig = getStatusConfig(order.status);
          return <div key={order.id} data-id="element-14486">
                <div className={`p-4 cursor-pointer ${isExpanded ? 'bg-slate-50' : ''}`} onClick={() => setExpandedOrderId(isExpanded ? null : order.id)} data-id="element-14487">
                  <div className="flex justify-between items-start mb-2" data-id="element-14488">
                    <div data-id="element-14489">
                      <div className="font-bold text-slate-900 text-sm" data-id="element-14490">
                        {order.name}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5" data-id="element-14491">
                        {getTypeLabel(order.type)} · {order.id}
                      </div>
                    </div>
                    <Badge variant={statusConfig.variant} size="sm" data-id="element-14492">
                      {statusConfig.label}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-14493">
                    <span className="text-xs text-slate-500" data-id="element-14494">
                      {order.startDate}
                      {order.endDate ? ` — ${order.endDate}` : ''}
                    </span>
                    <span className="font-bold text-slate-900" data-id="element-14495">
                      {order.price.toLocaleString('ru-RU')} ₽
                    </span>
                  </div>
                </div>

                {isExpanded && <div className="p-4 bg-slate-50 border-t border-slate-100 animate-in fade-in slide-in-from-top-2" data-id="element-14496">
                    {order.status === 'pending' || order.status === 'pending_moderation' || order.status === 'pending_payment' || order.status === 'rejected' ? <div className="space-y-3" data-id="element-14497">
                        <div className="flex items-center gap-2" data-id="element-14498">
                          {order.status === 'rejected' ? <AlertCircle className="w-4 h-4 text-rose-500" data-id="element-14499" /> : order.status === 'pending_payment' ? <Wallet className="w-4 h-4 text-indigo-500" data-id="element-14500" /> : <AlertCircle className="w-4 h-4 text-amber-500" data-id="element-14501" />}
                          <h4 className="font-bold text-slate-900 text-sm" data-id="element-14502">
                            {order.status === 'rejected' ? 'Отклонён' : order.status === 'pending_payment' ? 'Ожидает оплаты' : 'На модерации'}
                          </h4>
                        </div>

                        {order.status === 'rejected' && order.rejectionReason && <div className="bg-rose-50 border border-rose-200 rounded-lg p-2.5 text-xs text-rose-700" data-id="element-14503">
                              <div className="font-bold mb-0.5" data-id="element-14504">Причина:</div>
                              {order.rejectionReason}
                            </div>}

                        <div className="relative pt-1 pb-1" data-id="element-14505">
                          <div className="absolute left-3 top-5 bottom-3 w-0.5 bg-slate-200" data-id="element-14506"></div>

                          <div className="flex gap-3 mb-2.5 relative z-10" data-id="element-14507">
                            <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0" data-id="element-14508">
                              <Check className="w-3 h-3" data-id="element-14509" />
                            </div>
                            <div data-id="element-14510">
                              <div className="font-bold text-xs text-slate-900" data-id="element-14511">
                                Оформлен
                              </div>
                              <div className="text-[10px] text-slate-500" data-id="element-14512">
                                {order.startDate}
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-3 mb-2.5 relative z-10" data-id="element-14513">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'rejected' ? 'bg-rose-100 text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-14514">
                              {order.status === 'rejected' ? <AlertCircle className="w-3 h-3" data-id="element-14515" /> : order.status === 'pending_payment' || order.status === 'approved' ? <Check className="w-3 h-3" data-id="element-14516" /> : <Clock className="w-3 h-3" data-id="element-14517" />}
                            </div>
                            <div data-id="element-14518">
                              <div className="font-bold text-xs text-slate-900" data-id="element-14519">
                                Модерация
                              </div>
                              <div className={`text-[10px] ${order.status === 'rejected' ? 'text-rose-600' : order.status === 'pending_payment' || order.status === 'approved' ? 'text-emerald-600' : 'text-amber-600'}`} data-id="element-14520">
                                {order.status === 'rejected' ? `Отклонено ${order.moderatedAt || ''}` : order.status === 'pending_payment' || order.status === 'approved' ? `Одобрено ${order.moderatedAt || ''}` : 'В процессе...'}
                              </div>
                            </div>
                          </div>

                          <div className={`flex gap-3 mb-2.5 relative z-10 ${order.status === 'rejected' || order.status === 'pending' || order.status === 'pending_moderation' ? 'opacity-40' : ''}`} data-id="element-14521">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${order.status === 'pending_payment' ? 'bg-indigo-100 text-indigo-600 ring-2 ring-indigo-300 ring-offset-1' : 'bg-slate-100 text-slate-400'}`} data-id="element-14522">
                              <Wallet className="w-3 h-3" data-id="element-14523" />
                            </div>
                            <div data-id="element-14524">
                              <div className="font-bold text-xs text-slate-900" data-id="element-14525">
                                Оплата
                              </div>
                              <div className={`text-[10px] ${order.status === 'pending_payment' ? 'text-indigo-600 font-semibold' : 'text-slate-500'}`} data-id="element-14526">
                                {order.status === 'pending_payment' ? 'Ожидает оплаты' : 'Ожидается'}
                              </div>
                            </div>
                          </div>

                          <div className="flex gap-3 relative z-10 opacity-40" data-id="element-14527">
                            <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center shrink-0" data-id="element-14528">
                              <Rocket className="w-3 h-3" data-id="element-14529" />
                            </div>
                            <div data-id="element-14530">
                              <div className="font-bold text-xs text-slate-900" data-id="element-14531">
                                Запуск
                              </div>
                              <div className="text-[10px] text-slate-500" data-id="element-14532">
                                Ожидается
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Материалы рекламы — мобильная версия */}
                        {(order.adTitle || order.adImageUrl) && <div className="pt-3 border-t border-slate-200" data-id="element-14533">
                            <div className="flex items-center gap-1.5 mb-2" data-id="element-14534">
                              <FileText className="w-3.5 h-3.5 text-slate-400" data-id="element-14535" />
                              <span className="font-bold text-[10px] text-slate-600 uppercase tracking-wider" data-id="element-14536">
                                Материалы
                              </span>
                            </div>
                            <div className="rounded-lg border border-slate-200 overflow-hidden" data-id="element-14537">
                              {order.adImageUrl && <div className="h-24 bg-slate-100" data-id="element-14538">
                                  <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-14539" />
                                </div>}
                              <div className="p-2.5" data-id="element-14540">
                                {order.adTitle && <div className="font-bold text-xs text-slate-900 mb-0.5 truncate" data-id="element-14541">
                                    {order.adTitle}
                                  </div>}
                                {order.adDescription && <p className="text-[10px] text-slate-600 mb-1.5 line-clamp-2" data-id="element-14542">
                                    {order.adDescription}
                                  </p>}
                                {order.adLink && <div className="flex items-center gap-1 text-[10px] text-indigo-600 font-medium" data-id="element-14543">
                                    <Globe className="w-2.5 h-2.5" data-id="element-14544" />
                                    <span className="truncate" data-id="element-14545">
                                      {order.adLink}
                                    </span>
                                  </div>}
                              </div>
                            </div>
                          </div>}

                        <div className="flex flex-wrap gap-2" data-id="element-14546">
                          {order.status === 'pending_payment' && <Button variant="primary" size="sm" fullWidth icon={<Wallet className="w-3.5 h-3.5" data-id="element-14548" />} onClick={e => {
                    e.stopPropagation();
                    setAdOrders(prev => prev.map(o => o.id === order.id ? {
                      ...o,
                      status: 'active' as const
                    } : o));
                    setAdBalance(prev => prev - order.price);
                    showToast(`Оплата ${order.price.toLocaleString('ru-RU')} ₽ прошла. Реклама запущена!`);
                  }} data-id="element-14547">
                              Оплатить {order.price.toLocaleString('ru-RU')} ₽
                            </Button>}
                          {order.status === 'rejected' && <Button variant="primary" size="sm" fullWidth icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-14550" />} onClick={e => {
                    e.stopPropagation();
                    setAdOrders(prev => prev.map(o => o.id === order.id ? {
                      ...o,
                      status: 'pending_moderation' as const,
                      rejectionReason: undefined
                    } : o));
                    showToast('Заявка отправлена повторно на модерацию');
                  }} data-id="element-14549">
                              Исправить и отправить
                            </Button>}
                        </div>
                      </div> : <>
                        <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-14551">
                          <div data-id="element-14552">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14553">
                              Просмотры
                            </div>
                            <div className="font-bold text-slate-900" data-id="element-14554">
                              {order.views.toLocaleString('ru-RU')}
                            </div>
                          </div>
                          <div data-id="element-14555">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14556">
                              Клики
                            </div>
                            <div className="font-bold text-slate-900" data-id="element-14557">
                              {order.clicks.toLocaleString('ru-RU')}
                            </div>
                          </div>
                          <div data-id="element-14558">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14559">
                              Заказы
                            </div>
                            <div className="font-bold text-indigo-600" data-id="element-14560">
                              {order.orders}
                            </div>
                          </div>
                          <div data-id="element-14561">
                            <div className="text-[10px] text-slate-500 uppercase font-bold" data-id="element-14562">
                              Доход
                            </div>
                            <div className="font-bold text-emerald-600" data-id="element-14563">
                              {order.income.toLocaleString('ru-RU')} ₽
                            </div>
                          </div>
                        </div>

                        {/* Материалы рекламы — мобильная версия (активные/завершённые) */}
                        {(order.adTitle || order.adImageUrl) && <div className="pt-3 mb-3 border-t border-slate-200" data-id="element-14564">
                            <div className="flex items-center gap-1.5 mb-2" data-id="element-14565">
                              <FileText className="w-3.5 h-3.5 text-slate-400" data-id="element-14566" />
                              <span className="font-bold text-[10px] text-slate-600 uppercase tracking-wider" data-id="element-14567">
                                Материалы
                              </span>
                            </div>
                            <div className="rounded-lg border border-slate-200 overflow-hidden" data-id="element-14568">
                              {order.adImageUrl && <div className="h-24 bg-slate-100" data-id="element-14569">
                                  <img src={order.adImageUrl} alt={order.adTitle || 'Баннер'} className="w-full h-full object-cover" data-id="element-14570" />
                                </div>}
                              <div className="p-2.5" data-id="element-14571">
                                {order.adTitle && <div className="font-bold text-xs text-slate-900 mb-0.5 truncate" data-id="element-14572">
                                    {order.adTitle}
                                  </div>}
                                {order.adDescription && <p className="text-[10px] text-slate-600 mb-1.5 line-clamp-2" data-id="element-14573">
                                    {order.adDescription}
                                  </p>}
                                {order.adLink && <div className="flex items-center gap-1 text-[10px] text-indigo-600 font-medium" data-id="element-14574">
                                    <Globe className="w-2.5 h-2.5" data-id="element-14575" />
                                    <span className="truncate" data-id="element-14576">
                                      {order.adLink}
                                    </span>
                                  </div>}
                              </div>
                            </div>
                          </div>}

                        <div className="flex gap-2" data-id="element-14577">
                          <Button variant="primary" size="sm" className="flex-1" icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-14579" />} onClick={e => {
                    e.stopPropagation();
                    handleRepeatOrder(order);
                  }} data-id="element-14578">
                            Повторить
                          </Button>
                          {order.status === 'active' && <Button variant="outline" size="sm" className="flex-1" icon={<CalendarPlus className="w-3.5 h-3.5" data-id="element-14581" />} onClick={e => {
                    e.stopPropagation();
                    handleExtendOrder(order);
                  }} data-id="element-14580">
                              Продлить
                            </Button>}
                        </div>
                      </>}
                  </div>}
              </div>;
        })}
        </div>

        {/* Ad Orders Pagination */}
        {adOrdersTotalPages > 1 && <div className="p-4 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3" data-id="element-14582">
            <span className="text-xs text-slate-500" data-id="element-14583">
              Показано {(adOrdersPage - 1) * AD_ORDERS_PER_PAGE + 1}–
              {Math.min(adOrdersPage * AD_ORDERS_PER_PAGE, adOrders.length)} из{' '}
              {adOrders.length}
            </span>
            <Pagination currentPage={adOrdersPage} totalPages={adOrdersTotalPages} onPageChange={setAdOrdersPage} data-id="element-14584" />
          </div>}
      </Card>

      {/* Section 5: Tariff Plans */}
      <h3 className="text-lg font-bold text-slate-900 mt-2" data-id="element-14585">Тарифные планы</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-14586">
        {/* Basic */}
        <Card padding="md" className="flex flex-col border-slate-200" data-id="element-14587">
          <div className="mb-4" data-id="element-14588">
            <h4 className="text-lg font-bold text-slate-900 mb-1" data-id="element-14589">Базовый</h4>
            <div className="text-2xl font-bold text-slate-900" data-id="element-14590">Бесплатно</div>
            <p className="text-xs text-slate-500 mt-1" data-id="element-14591">~5-10 заказов/мес</p>
          </div>
          <ul className="space-y-2 mb-6 flex-1" data-id="element-14592">
            {['Профиль в каталоге', 'До 3 услуг', 'Стандартная позиция'].map(f => <li key={f} className="flex items-center gap-2 text-sm text-slate-600" data-id="element-14593">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" data-id="element-14594" />{' '}
                  {f}
                </li>)}
          </ul>
          <div className="py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-center text-sm font-medium text-slate-600 flex items-center justify-center gap-2" data-id="element-14595">
            <CheckCircle2 className="w-4 h-4 text-emerald-500" data-id="element-14596" /> Ваш текущий
            план
          </div>
        </Card>

        {/* PRO */}
        <Card padding="md" className="flex flex-col border-indigo-200 shadow-md ring-1 ring-indigo-100 relative" data-id="element-14597">
          <div className="absolute top-0 right-0 bg-indigo-500 text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg rounded-tr-xl uppercase tracking-wider" data-id="element-14598">
            Популярный
          </div>
          <div className="mb-4" data-id="element-14599">
            <h4 className="text-lg font-bold text-indigo-700 mb-1 flex items-center gap-2" data-id="element-14600">
              PRO <ShieldCheck className="w-5 h-5" data-id="element-14601" />
            </h4>
            <div className="flex items-baseline gap-1" data-id="element-14602">
              <span className="text-2xl font-bold text-slate-900" data-id="element-14603">3 000 ₽</span>
              <span className="text-slate-500 text-sm" data-id="element-14604">/ мес</span>
            </div>
            <p className="text-xs text-emerald-600 font-medium mt-1" data-id="element-14605">
              ~20-35 заказов/мес (+40%)
            </p>
          </div>
          <ul className="space-y-2 mb-6 flex-1" data-id="element-14606">
            {['Бейдж PRO', 'Приоритет в поиске', 'До 10 услуг', 'Расширенная аналитика', 'Поддержка в чате'].map(f => <li key={f} className="flex items-center gap-2 text-sm text-slate-600" data-id="element-14607">
                <Check className="w-4 h-4 text-indigo-500 flex-shrink-0" data-id="element-14608" /> {f}
              </li>)}
          </ul>
          <Button variant="primary" fullWidth onClick={() => {
          setSelectedTariff('pro');
          setShowTariffModal(true);
        }} data-id="element-14609">
            Перейти на PRO
          </Button>
        </Card>

        {/* TOP */}
        <Card padding="md" className="flex flex-col bg-gradient-to-b from-amber-50/50 to-white border-amber-200 relative" data-id="element-14610">
          <div className="mb-4" data-id="element-14611">
            <h4 className="text-lg font-bold text-amber-700 mb-1 flex items-center gap-2" data-id="element-14612">
              TOP <Crown className="w-5 h-5" data-id="element-14613" />
            </h4>
            <div className="flex items-baseline gap-1" data-id="element-14614">
              <span className="text-2xl font-bold text-slate-900" data-id="element-14615">7 000 ₽</span>
              <span className="text-slate-500 text-sm" data-id="element-14616">/ мес</span>
            </div>
            <p className="text-xs text-emerald-600 font-medium mt-1" data-id="element-14617">
              ~50-80 заказов/мес (+120%)
            </p>
          </div>
          <ul className="space-y-2 mb-6 flex-1" data-id="element-14618">
            {['Всё из PRO', 'Бейдж TOP Астролог', 'Закреп вверху каталога', 'Блок на главной', 'Безлимит услуг', 'Персональный менеджер'].map(f => <li key={f} className="flex items-center gap-2 text-sm text-slate-600" data-id="element-14619">
                <Check className="w-4 h-4 text-amber-500 flex-shrink-0" data-id="element-14620" /> {f}
              </li>)}
          </ul>
          <Button variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" fullWidth onClick={() => {
          setSelectedTariff('top');
          setShowTariffModal(true);
        }} data-id="element-14621">
            Перейти на TOP
          </Button>
        </Card>
      </div>

      {/* Section 6: Active Placements */}
      <Card padding="md" data-id="element-14622">
        <div className="flex justify-between items-center mb-4" data-id="element-14623">
          <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-14624">
            <Target className="w-5 h-5 text-amber-500" data-id="element-14625" /> Где вы размещены
          </h3>
          <Button variant="outline" size="sm" onClick={() => {
          setShowPinModal(true);
          setSelectedPinCategory('Натальная карта');
        }} data-id="element-14626">
            Купить размещение
          </Button>
        </div>

        {activePlacements.length > 0 ? <div className="space-y-3" data-id="element-14627">
            {activePlacements.map((p, i) => <div key={i} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-xl border border-amber-100 bg-amber-50/30" data-id="element-14628">
                <div className="flex items-center gap-3" data-id="element-14629">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center" data-id="element-14630">
                    <Target className="w-4 h-4 text-amber-600" data-id="element-14631" />
                  </div>
                  <div data-id="element-14632">
                    <div className="font-bold text-slate-900 text-sm" data-id="element-14633">
                      {p.type}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-14634">{p.category}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm" data-id="element-14635">
                  <div className="flex items-center gap-1 text-slate-600" data-id="element-14636">
                    <Clock className="w-3.5 h-3.5 text-amber-500" data-id="element-14637" />
                    <span data-id="element-14638">{p.daysLeft} дн.</span>
                  </div>
                  <div className="flex items-center gap-1 text-slate-600" data-id="element-14639">
                    <Eye className="w-3.5 h-3.5 text-indigo-500" data-id="element-14640" />
                    <span data-id="element-14641">{p.views.toLocaleString('ru-RU')}</span>
                  </div>
                  <Button variant="ghost" size="sm" className="text-indigo-600 hover:bg-indigo-50 h-8 px-2" onClick={() => {
              setShowPinModal(true);
              setSelectedPinCategory(p.category);
            }} data-id="element-14642">
                    Продлить
                  </Button>
                </div>
              </div>)}
          </div> : <div className="text-center py-8 text-slate-500" data-id="element-14643">
            <Target className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-14644" />
            <p className="text-sm" data-id="element-14645">У вас нет активных размещений</p>
          </div>}
      </Card>

      {/* Section 7: Simple Analytics */}
      <Card padding="md" data-id="element-14646">
        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-14647">
          <BarChart3 className="w-5 h-5 text-indigo-500" data-id="element-14648" /> Эффективность за
          месяц
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-14649">
          <div className="text-center p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-14650">
            <Eye className="w-5 h-5 text-slate-400 mx-auto mb-2" data-id="element-14651" />
            <div className="text-2xl font-bold text-slate-900" data-id="element-14652">1 247</div>
            <div className="text-xs text-slate-500 mt-1" data-id="element-14653">Просмотры</div>
          </div>
          <div className="text-center p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-14654">
            <MousePointerClick className="w-5 h-5 text-indigo-400 mx-auto mb-2" data-id="element-14655" />
            <div className="text-2xl font-bold text-slate-900" data-id="element-14656">620</div>
            <div className="text-xs text-slate-500 mt-1" data-id="element-14657">Клики</div>
          </div>
          <div className="text-center p-4 bg-indigo-50 rounded-xl border border-indigo-100" data-id="element-14658">
            <ShoppingBag className="w-5 h-5 text-indigo-500 mx-auto mb-2" data-id="element-14659" />
            <div className="text-2xl font-bold text-indigo-700" data-id="element-14660">39</div>
            <div className="text-xs text-indigo-600 mt-1" data-id="element-14661">Заказы</div>
          </div>
          <div className="text-center p-4 bg-emerald-50 rounded-xl border border-emerald-100" data-id="element-14662">
            <DollarSign className="w-5 h-5 text-emerald-500 mx-auto mb-2" data-id="element-14663" />
            <div className="text-2xl font-bold text-emerald-700" data-id="element-14664">117 000 ₽</div>
            <div className="text-xs text-emerald-600 mt-1" data-id="element-14665">Доход</div>
          </div>
        </div>
      </Card>

      {/* Tariff Upgrade Modal */}
      {showTariffModal && selectedTariff && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-14666">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-14667">
            <div className="flex justify-between items-center p-5 border-b border-slate-100" data-id="element-14668">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-14669">
                Переход на {selectedTariff === 'pro' ? 'PRO' : 'TOP'}
              </h3>
              <button onClick={() => setShowTariffModal(false)} className="text-slate-400 hover:text-slate-600" data-id="element-14670">
                <X className="w-5 h-5" data-id="element-14671" />
              </button>
            </div>

            <div className="p-5 space-y-5" data-id="element-14672">
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-14673">
                <div className="flex justify-between text-sm mb-3" data-id="element-14674">
                  <span className="text-slate-500" data-id="element-14675">Тариф</span>
                  <span className="font-bold text-slate-900" data-id="element-14676">
                    {selectedTariff === 'pro' ? 'PRO' : 'TOP'}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-3" data-id="element-14677">
                  <span className="text-slate-500" data-id="element-14678">Стоимость</span>
                  <span className="font-bold text-slate-900" data-id="element-14679">
                    {selectedTariff === 'pro' ? '3 000' : '7 000'} ₽ / мес
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-3" data-id="element-14680">
                  <span className="text-slate-500" data-id="element-14681">Ожидаемый рост заказов</span>
                  <span className="font-bold text-emerald-600" data-id="element-14682">
                    {selectedTariff === 'pro' ? '+40%' : '+120%'}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-3 border-t border-slate-200" data-id="element-14683">
                  <span className="font-bold text-slate-900" data-id="element-14684">
                    К оплате сейчас
                  </span>
                  <span className="text-xl font-bold text-indigo-600" data-id="element-14685">
                    {selectedTariff === 'pro' ? '3 000' : '7 000'} ₽
                  </span>
                </div>
              </div>

              <p className="text-xs text-slate-500" data-id="element-14686">
                Оплата будет списана с вашего баланса. Тариф активируется
                мгновенно. Отмена возможна в любое время.
              </p>

              <div className="flex gap-3" data-id="element-14687">
                <Button variant="outline" fullWidth onClick={() => setShowTariffModal(false)} data-id="element-14688">
                  Отмена
                </Button>
                <Button variant="primary" fullWidth onClick={() => handleUpgradeTariff(selectedTariff)} data-id="element-14689">
                  Оплатить и активировать
                </Button>
              </div>
            </div>
          </div>
        </div>}

      {/* New Ad Order Modal */}
      {showNewAdModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-14690">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto" data-id="element-14691">
            <div className="flex justify-between items-center p-5 border-b border-slate-100 sticky top-0 bg-white z-10" data-id="element-14692">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-14693">
                Создать рекламу
              </h3>
              <button onClick={() => {
            setShowNewAdModal(false);
            setSelectedAdSlot(null);
            setAdWizardStep(1);
            setAdMaterials({
              title: '',
              description: '',
              link: '',
              imageUrl: ''
            });
          }} className="text-slate-400 hover:text-slate-600" data-id="element-14694">
                <X className="w-5 h-5" data-id="element-14695" />
              </button>
            </div>
            <div className="p-5 space-y-4" data-id="element-14696">
              {/* Progress Steps */}
              <div className="flex items-center justify-between mb-6 relative" data-id="element-14697">
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 bg-slate-100 -z-10" data-id="element-14698"></div>
                <div className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 bg-indigo-500 -z-10 transition-all duration-300" style={{
              width: `${(adWizardStep - 1) * 50}%`
            }} data-id="element-14699"></div>

                {[1, 2, 3].map(step => <div key={step} className="flex flex-col items-center gap-1 bg-white px-2" data-id="element-14700">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${adWizardStep >= step ? 'bg-indigo-600 text-white shadow-md' : 'bg-slate-100 text-slate-400'}`} data-id="element-14701">
                      {adWizardStep > step ? <Check className="w-4 h-4" data-id="element-14702" /> : step}
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider font-bold ${adWizardStep >= step ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-14703">
                      {step === 1 ? 'Формат' : step === 2 ? 'Материалы' : 'Оплата'}
                    </span>
                  </div>)}
              </div>

              {adWizardStep === 1 && <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-14704">
                  <p className="text-sm text-slate-600" data-id="element-14705">
                    Выберите рекламный слот. Каждый формат показывается в разных
                    местах сайта.
                  </p>

                  <div className="max-h-[45vh] overflow-y-auto space-y-3 pr-1 -mr-1" data-id="element-14706">
                    {availableAdSlots.map(slot => <button key={slot.id} onClick={() => setSelectedAdSlot(selectedAdSlot === slot.id ? null : slot.id)} className={`w-full text-left p-4 rounded-xl border transition-all ${selectedAdSlot === slot.id ? slot.activeCls : 'border-slate-200 hover:border-slate-300'}`} data-id="element-14707">
                        <div className="flex items-start gap-3" data-id="element-14708">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${slot.iconCls}`} data-id="element-14709">
                            <slot.icon className="w-5 h-5" data-id="element-14710" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-14711">
                            <div className="flex items-center justify-between gap-2" data-id="element-14712">
                              <span className="font-bold text-slate-900 text-sm" data-id="element-14713">
                                {slot.name}
                              </span>
                              <span className="text-xs font-bold text-indigo-600 whitespace-nowrap" data-id="element-14714">
                                {slot.price}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-0.5" data-id="element-14715">
                              {slot.where}
                            </p>
                            {selectedAdSlot === slot.id && <div className="mt-3 pt-3 border-t border-slate-100 space-y-2 animate-in fade-in" data-id="element-14716">
                                <div className="flex items-center gap-2 text-xs" data-id="element-14717">
                                  <Eye className="w-3.5 h-3.5 text-slate-400" data-id="element-14718" />
                                  <span className="text-slate-600" data-id="element-14719">
                                    Охват:{' '}
                                    <strong className="text-slate-900" data-id="element-14720">
                                      {slot.reach}
                                    </strong>
                                  </span>
                                </div>
                                <div className="flex items-start gap-2 text-xs" data-id="element-14721">
                                  <AlertCircle className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-14722" />
                                  <span className="text-slate-600" data-id="element-14723">
                                    Требования:{' '}
                                    <strong className="text-slate-900" data-id="element-14724">
                                      {slot.requirements}
                                    </strong>
                                  </span>
                                </div>
                              </div>}
                          </div>
                        </div>
                      </button>)}
                  </div>

                  <div className="flex justify-end pt-2" data-id="element-14725">
                    <Button variant="primary" disabled={!selectedAdSlot} onClick={() => {
                const selected = availableAdSlots.find(s => s.id === selectedAdSlot);
                if (selected?.slotType === 'Pinned') {
                  setShowNewAdModal(false);
                  setSelectedAdSlot(null);
                  setShowPinModal(true);
                  setSelectedPinCategory('Натальная карта');
                } else {
                  setAdWizardStep(2);
                }
              }} icon={<ArrowRight className="w-4 h-4" data-id="element-14727" />} data-id="element-14726">
                      Далее
                    </Button>
                  </div>
                </div>}

              {adWizardStep === 2 && <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-14728">
                  <div data-id="element-14729">
                    <label className="block text-sm font-bold text-slate-700 mb-1" data-id="element-14730">
                      Заголовок рекламы
                    </label>
                    <input type="text" value={adMaterials.title} onChange={e => setAdMaterials({
                ...adMaterials,
                title: e.target.value
              })} placeholder="Например: Профессиональный разбор натальной карты" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-14731" />
                    <div className="text-right text-xs text-slate-400 mt-1" data-id="element-14732">
                      {adMaterials.title.length}/50
                    </div>
                  </div>

                  <div data-id="element-14733">
                    <label className="block text-sm font-bold text-slate-700 mb-1" data-id="element-14734">
                      Описание
                    </label>
                    <textarea value={adMaterials.description} onChange={e => setAdMaterials({
                ...adMaterials,
                description: e.target.value
              })} placeholder="Кратко опишите ваше предложение..." rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none" data-id="element-14735" />
                  </div>

                  <div data-id="element-14736">
                    <label className="block text-sm font-bold text-slate-700 mb-1" data-id="element-14737">
                      Ссылка (куда вести)
                    </label>
                    <input type="text" value={adMaterials.link} onChange={e => setAdMaterials({
                ...adMaterials,
                link: e.target.value
              })} placeholder="https://astroguide.ru/profile/elena" className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-id="element-14738" />
                  </div>

                  <div data-id="element-14739">
                    <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-14740">
                      Баннер / Изображение
                    </label>
                    <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:bg-slate-50 hover:border-indigo-300 cursor-pointer transition-colors relative overflow-hidden group" onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.onchange = e => {
                  const file = (e.target as HTMLInputElement).files?.[0];
                  if (file) {
                    const url = URL.createObjectURL(file);
                    setAdMaterials({
                      ...adMaterials,
                      imageUrl: url
                    });
                    showToast(`Изображение загружено ✓`);
                  }
                };
                input.click();
              }} data-id="element-14741">
                      {adMaterials.imageUrl ? <>
                          <img src={adMaterials.imageUrl} alt="Banner" className="absolute inset-0 w-full h-full object-cover" data-id="element-14742" />
                          <div className="absolute inset-0 bg-slate-900/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-14743">
                            <span className="text-white font-medium text-sm" data-id="element-14744">
                              Заменить изображение
                            </span>
                          </div>
                        </> : <>
                          <LayoutGrid className="w-8 h-8 text-slate-400 mx-auto mb-2" data-id="element-14745" />
                          <div className="text-sm font-bold text-slate-700 mb-1" data-id="element-14746">
                            Загрузить изображение
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-14747">
                            JPG, PNG до 5 МБ
                          </div>
                        </>}
                    </div>
                  </div>

                  <div className="flex justify-between pt-4 border-t border-slate-100" data-id="element-14748">
                    <Button variant="outline" onClick={() => setAdWizardStep(1)} data-id="element-14749">
                      Назад
                    </Button>
                    <Button variant="primary" disabled={!adMaterials.title || !adMaterials.imageUrl} onClick={() => setAdWizardStep(3)} icon={<ArrowRight className="w-4 h-4" data-id="element-14751" />} data-id="element-14750">
                      Далее
                    </Button>
                  </div>
                </div>}

              {adWizardStep === 3 && (() => {
            const selectedSlot = availableAdSlots.find(s => s.id === selectedAdSlot);
            const price = selectedSlot?.priceNum || 0;
            const slotName = selectedSlot?.name || 'Реклама';
            const slotType = selectedSlot?.slotType === 'Email' ? 'email' : selectedSlot?.slotType === 'Pinned' ? 'pin' : 'banner';
            return <div className="space-y-4 animate-in slide-in-from-right-4 duration-300" data-id="element-14752">
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-14753">
                        <h4 className="font-bold text-slate-900 mb-3" data-id="element-14754">
                          Сводка заказа
                        </h4>

                        <div className="space-y-2 mb-4" data-id="element-14755">
                          <div className="flex justify-between text-sm" data-id="element-14756">
                            <span className="text-slate-500" data-id="element-14757">Формат</span>
                            <span className="font-medium text-slate-900" data-id="element-14758">
                              {slotName}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm" data-id="element-14759">
                            <span className="text-slate-500" data-id="element-14760">Период</span>
                            <span className="font-medium text-slate-900" data-id="element-14761">
                              1 неделя
                            </span>
                          </div>
                        </div>

                        <div className="p-3 bg-white rounded-lg border border-slate-200 mb-4" data-id="element-14762">
                          <div className="text-xs text-slate-500 mb-2" data-id="element-14763">
                            Превью:
                          </div>
                          <div className="flex gap-3 items-center" data-id="element-14764">
                            <div className="w-16 h-12 rounded bg-slate-100 overflow-hidden flex-shrink-0" data-id="element-14765">
                              {adMaterials.imageUrl && <img src={adMaterials.imageUrl} alt="Preview" className="w-full h-full object-cover" data-id="element-14766" />}
                            </div>
                            <div className="min-w-0" data-id="element-14767">
                              <div className="font-bold text-sm text-slate-900 truncate" data-id="element-14768">
                                {adMaterials.title}
                              </div>
                              <div className="text-xs text-slate-500 truncate" data-id="element-14769">
                                {adMaterials.description || 'Без описания'}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-between items-center pt-3 border-t border-slate-200" data-id="element-14770">
                          <span className="font-bold text-slate-900" data-id="element-14771">
                            К оплате
                          </span>
                          <span className="text-xl font-bold text-indigo-600" data-id="element-14772">
                            {price.toLocaleString('ru-RU')} ₽
                          </span>
                        </div>
                      </div>

                      <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100" data-id="element-14773">
                        <div className="flex flex-wrap justify-between items-center gap-2 mb-2" data-id="element-14774">
                          <span className="text-sm font-bold text-indigo-900 flex items-center gap-2" data-id="element-14775">
                            <Wallet className="w-4 h-4" data-id="element-14776" /> Рекламный баланс
                          </span>
                          <span className="font-bold text-indigo-700" data-id="element-14777">
                            {adBalance.toLocaleString('ru-RU')} ₽
                          </span>
                        </div>

                        {adBalance < price ? <div className="text-xs text-rose-600 mt-2 flex items-start gap-1" data-id="element-14778">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" data-id="element-14779" />
                            <span data-id="element-14780">
                              Недостаточно средств. Пополните баланс на{' '}
                              {(price - adBalance).toLocaleString('ru-RU')} ₽
                            </span>
                          </div> : <div className="text-xs text-indigo-600 mt-2" data-id="element-14781">
                            Остаток после оплаты:{' '}
                            {(adBalance - price).toLocaleString('ru-RU')} ₽
                          </div>}
                      </div>

                      <div className="flex justify-between pt-4 border-t border-slate-100" data-id="element-14782">
                        <Button variant="outline" onClick={() => setAdWizardStep(2)} data-id="element-14783">
                          Назад
                        </Button>
                        <Button variant="primary" disabled={adBalance < price} onClick={() => {
                  const newOrder: AdOrder = {
                    id: `ORD-A-00${adOrders.length + 1}`,
                    type: slotType as any,
                    name: slotName,
                    status: 'pending',
                    startDate: new Date().toLocaleDateString('ru-RU'),
                    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('ru-RU'),
                    price: price,
                    views: 0,
                    clicks: 0,
                    orders: 0,
                    income: 0
                  };
                  setAdOrders([newOrder, ...adOrders]);
                  setAdBalance(adBalance - price);
                  setShowNewAdModal(false);
                  setSelectedAdSlot(null);
                  setAdWizardStep(1);
                  setAdMaterials({
                    title: '',
                    description: '',
                    link: '',
                    imageUrl: ''
                  });
                  showToast('Заказ оплачен и отправлен на модерацию. Вы получите уведомление о статусе.');
                }} data-id="element-14784">
                          Оплатить и отправить
                        </Button>
                      </div>
                    </div>;
          })()}
            </div>
          </div>
        </div>}

      {/* Pin Purchase Modal */}
      {showPinModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200" data-id="element-14785">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200" data-id="element-14786">
            <div className="flex justify-between items-center p-5 border-b border-slate-100" data-id="element-14787">
              <h3 className="text-xl font-bold text-slate-900" data-id="element-14788">
                Купить размещение
              </h3>
              <button onClick={() => setShowPinModal(false)} className="text-slate-400 hover:text-slate-600" data-id="element-14789">
                <X className="w-5 h-5" data-id="element-14790" />
              </button>
            </div>

            <div className="p-5 space-y-5" data-id="element-14791">
              <div data-id="element-14792">
                <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-14793">
                  Категория
                </label>
                <select className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" value={selectedPinCategory} onChange={e => setSelectedPinCategory(e.target.value)} data-id="element-14794">
                  <option value="Натальная карта" data-id="element-14795">Натальная карта</option>
                  <option value="Совместимость" data-id="element-14796">Совместимость</option>
                  <option value="Соляр" data-id="element-14797">Соляр на год</option>
                  <option value="Транзиты" data-id="element-14798">Транзиты</option>
                </select>
              </div>

              <div data-id="element-14799">
                <label className="block text-sm font-bold text-slate-700 mb-2" data-id="element-14800">
                  Длительность
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-14801">
                  {[{
                value: '1week' as const,
                label: '1 неделя',
                price: '2 000 ₽'
              }, {
                value: '2weeks' as const,
                label: '2 недели',
                price: '3 500 ₽'
              }, {
                value: '1month' as const,
                label: '1 месяц',
                price: '6 000 ₽'
              }].map(opt => <button key={opt.value} onClick={() => setSelectedPinDuration(opt.value)} className={`p-3 rounded-xl border text-center transition-all ${selectedPinDuration === opt.value ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500' : 'border-slate-200 hover:border-indigo-300'}`} data-id="element-14802">
                      <div className="text-sm font-bold text-slate-900" data-id="element-14803">
                        {opt.label}
                      </div>
                      <div className="text-xs text-indigo-600 font-medium mt-1" data-id="element-14804">
                        {opt.price}
                      </div>
                    </button>)}
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-14805">
                <div className="flex justify-between text-sm mb-2" data-id="element-14806">
                  <span className="text-slate-500" data-id="element-14807">Категория</span>
                  <span className="font-medium text-slate-900" data-id="element-14808">
                    {selectedPinCategory}
                  </span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-slate-200" data-id="element-14809">
                  <span className="font-bold text-slate-900" data-id="element-14810">К оплате</span>
                  <span className="text-xl font-bold text-indigo-600" data-id="element-14811">
                    {selectedPinDuration === '1week' ? '2 000' : selectedPinDuration === '2weeks' ? '3 500' : '6 000'}{' '}
                    ₽
                  </span>
                </div>
              </div>

              <div className="flex gap-3" data-id="element-14812">
                <Button variant="outline" fullWidth onClick={() => setShowPinModal(false)} data-id="element-14813">
                  Отмена
                </Button>
                <Button variant="primary" fullWidth onClick={handleBuyPin} data-id="element-14814">
                  Оплатить
                </Button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}